export const en = {
  common: {
    actions: {
      cancel: 'Cancel',
      create: 'Create',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      void: 'Void',
      add: 'Add',
      link: 'Link',
      back: 'Back',
      discard: 'Discard',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      voided: 'Voided',
      open: 'Open',
      inProgress: 'InProgress',
      draft: 'Draft',
      closed: 'Closed',
      fulfilled: 'Fulfilled',
      sent: 'Sent',
      accepted: 'Accepted',
      rejected: 'Rejected',
      expired: 'Expired',
      converted: 'Converted',
      default: 'Default',
    },
    loading: {
      creating: 'Creating...',
      saving: 'Saving...',
      linking: 'Linking...',
      adding: 'Adding...',
      converting: 'Converting...',
      loadingMore: 'Loading more...',
    },
    selected: 'selected',
    errors: {
      loading: (entity: string, message: string) =>
        `Error loading ${entity}: ${message}`,
      notFound: (entity: string) => `${entity} not found`,
      unknown: 'An unknown error occurred',
    },
    labels: {
      notes: 'Notes',
      noNotes: 'No notes',
      name: 'Name',
      status: 'Status',
      created: 'Created',
      item: 'Item',
      quantity: 'Quantity',
      unit: 'Unit',
      items: 'Items',
      description: 'Description',
    },
    placeholders: {
      optionalNotes: 'Optional notes...',
      optionalDescription: 'Optional description...',
      searchAccount: 'Search for an account...',
      selectItem: 'Search for an item...',
    },
    sections: {
      additionalInfo: 'Additional Information',
    },
  },
  pricing: {
    variables: {
      list: {
        title: 'Variables',
        search: 'Search variables...',
        ariaLabel: 'Variables list',
        createButton: 'Create Variable',
        empty: {
          title: 'No variables found',
          description:
            'Create variables to use in pricing formulas (e.g., $copperPrice).',
        },
      },
      create: {
        title: 'Create Variable',
        sections: {
          details: 'Variable Details',
        },
        fields: {
          name: 'Name',
          description: 'Description',
          value: 'Value',
        },
        placeholders: {
          name: 'e.g., copperPrice',
          description: 'Optional description...',
          value: '0.00',
        },
        submitButton: 'Create Variable',
      },
      edit: {
        title: 'Edit Variable',
        submitButton: 'Save Changes',
      },
      table: {
        columns: {
          name: 'Name',
          description: 'Description',
          value: 'Value',
          updatedBy: 'Last Updated By',
          created: 'Created',
        },
      },
      toast: {
        created: 'Variable created successfully',
        updated: 'Variable updated successfully',
        deleted: 'Variable deleted successfully',
        createError: (error: string) => `Failed to create variable: ${error}`,
        updateError: (error: string) => `Failed to update variable: ${error}`,
        deleteError: (error: string) => `Failed to delete variable: ${error}`,
      },
      validation: {
        nameRequired: 'Name is required',
        valueRequired: 'Value is required',
        nameFormat:
          'Name must start with a letter or digit and contain only letters, numbers, and underscores',
      },
      history: {
        title: 'Variable History',
        empty: 'No history recorded for this variable.',
        events: {
          created: 'Variable Created',
          updated: 'Variable Updated',
          deleted: 'Variable Deleted',
        },
        initialValue: 'Value:',
      },
      deleteConfirm: {
        message: (name: string) =>
          `Are you sure you want to delete variable "${name}"? This may affect pricing formulas that reference it.`,
      },
    },
    counterpartyGroups: {
      list: {
        title: 'Counterparty Groups',
        search: 'Search counterparty groups...',
        ariaLabel: 'Counterparty groups list',
        allOperationTypes: 'All Types',
        createButton: 'Create Group',
        empty: {
          title: 'No counterparty groups found',
          description:
            'Create counterparty groups to organize accounts for custom pricing.',
        },
      },
      detail: {
        title: 'Counterparty Group',
        backButton: 'Back to Counterparty Groups',
        tabs: {
          members: 'Members',
        },
        memberCount: (count: number) =>
          `${count} ${count === 1 ? 'member' : 'members'}`,
        subtitle: {
          created: 'Created',
          lastUpdatedBy: 'Last Updated By',
          on: 'on',
        },
        members: {
          empty: {
            title: 'No members',
            description:
              'Add accounts to this counterparty group to apply custom pricing.',
          },
          addButton: 'Add Accounts',
          removeButton: 'Remove',
          removeConfirm: 'Remove this account from the group?',
          accountNameHeader: 'Account',
          accountTypeHeader: 'Type',
          addedByHeader: 'Added By',
          addedDateHeader: 'Added',
          searchPlaceholder: 'Search accounts...',
          searchAriaLabel: 'Search accounts to add',
          addDialogTitle: 'Add Accounts',
          addDialogSubmit: 'Add Selected',
          noAccountsFound: 'No accounts found',
          alreadyMember: 'Already a member',
        },
      },
      create: {
        title: 'Create Counterparty Group',
        sections: {
          groupDetails: 'Group Details',
        },
        fields: {
          name: 'Name',
          operationType: 'Operation Type',
        },
        placeholders: {
          name: 'e.g., Premium Suppliers',
        },
        submitButton: 'Create Group',
      },
      edit: {
        title: 'Edit Counterparty Group',
        submitButton: 'Save Changes',
      },
      table: {
        columns: {
          name: 'Name',
          operationType: 'Operation Type',
          memberCount: 'Accounts',
          updatedAt: 'Updated',
          created: 'Created',
        },
      },
      toolbar: {
        selected: 'selected',
        delete: (count: number) =>
          `Delete ${count} ${count === 1 ? 'group' : 'groups'}`,
      },
      deleteConfirm: {
        message: (name: string) =>
          `Are you sure you want to delete "${name}"? This will remove all member associations.`,
      },
      toast: {
        created: 'Counterparty group created successfully',
        updated: 'Counterparty group updated successfully',
        deleted: 'Counterparty group deleted successfully',
        bulkDeleted: (count: number) =>
          `${count} counterparty ${count === 1 ? 'group' : 'groups'} deleted`,
        accountsAdded: 'Accounts added successfully',
        accountsRemoved: 'Accounts removed successfully',
        addAccountsError: (error: string) => `Failed to add accounts: ${error}`,
        createError: (error: string) =>
          `Failed to create counterparty group: ${error}`,
        updateError: (error: string) =>
          `Failed to update counterparty group: ${error}`,
        deleteError: (error: string) =>
          `Failed to delete counterparty group: ${error}`,
      },
      validation: {
        nameRequired: 'Name is required',
      },
    },
    defaultPriceList: {
      title: 'Default Price List',
      selectors: {
        facility: 'Facility',
        operationType: 'Operation Type',
        currency: 'Currency',
        selectFacility: 'Select a facility...',
        selectOperationType: 'Select operation type...',
        currencyPlaceholder: 'USD',
      },
      notes: {
        label: 'Notes',
        placeholder: 'Add notes...',
        saved: 'Notes saved',
      },
      productPricings: {
        title: 'Product Pricings',
        addButton: 'Add Product Pricing',
        empty: {
          title: 'No product pricings',
          description:
            'Add product pricings to define default prices for this facility and operation type.',
        },
      },
      effectiveDate: {
        label: 'Tiers Effective Date',
        discard: 'Discard',
      },
      toast: {
        notesSaved: 'Notes updated successfully',
        effectiveDateSaved: 'Effective date updated for all product pricings',
        productPricingAdded: 'Product pricing added',
        productPricingUpdated: 'Product pricing updated',
        productPricingDeleted: 'Product pricing deleted',
        error: (error: string) => `Operation failed: ${error}`,
      },
      prompt:
        'Select a facility, operation type, and currency to view or create the default price list.',
    },
    customPriceLists: {
      entityName: 'custom price lists',
      list: {
        title: 'Custom Price Lists',
        search: 'Search custom price lists...',
        ariaLabel: 'Custom price lists',
        createButton: 'Create Price List',
        empty: {
          title: 'No custom price lists found',
          description:
            'Create a draft custom price list for specific counterparty groups.',
          emptyTab: (tab: string) => `No ${tab.toLowerCase()} price lists`,
        },
        statusTabs: {
          all: 'All',
          active: 'Active',
          expired: 'Expired',
          archived: 'Archived',
          draft: 'Draft',
        },
      },
      detail: {
        title: 'Custom Price List',
        backButton: 'Back to Custom Price Lists',
        tabs: {
          pricings: 'Product Pricings',
        },
        pricingCount: (count: number) =>
          `${count} ${count === 1 ? 'pricing' : 'pricings'}`,
        subtitle: {
          created: 'Created',
          counterpartyGroup: 'Counterparty Group',
          startsOn: 'Starts on',
          lastUpdatedBy: 'Last Updated By',
          on: 'on',
        },
        actions: {
          publish: 'Publish',
          archive: 'Archive',
          unarchive: 'Unarchive',
          delete: 'Delete',
          edit: 'Edit Details',
          copyFrom: 'Copy From List',
          importFrom: 'Pull from another price list',
          actionsMenu: 'Actions',
          confirmDelete: {
            title: 'Delete Price List',
            description:
              'Are you sure you want to delete this price list? This action cannot be undone.',
            confirm: 'Delete',
          },
          duplicate: 'Duplicate',
          downloadPdf: 'Download PDF',
          sendEmail: 'Send via Email',
          viewHistory: 'View History',
        },
        effectiveDate: {
          label: 'Tiers Effective Date',
          discard: 'Discard',
        },
        toast: {
          effectiveDateSaved: 'Effective date updated',
          updated: 'Price list updated',
          updateError: (error: string) =>
            `Failed to update price list: ${error}`,
        },
        pricings: {
          empty: {
            title: 'No product pricings',
            description: 'Add product pricings to this custom price list.',
          },
          addButton: 'Add Product Pricing',
        },
      },
      create: {
        title: 'Create Price List',
        draftNote:
          'Price list will be saved as a draft. After materials and prices are added, it can be published.',
        sections: {
          details: 'Price List Details',
          dates: 'Effective Period',
        },
        fields: {
          name: 'Name',
          operationType: 'Operation Type',
          pricingMode: 'Pricing Mode',
          pricingModeStandard: 'Standard',
          pricingModeCRV: 'CRV',
          pricingModeStandardDescription:
            'Full pricing with min, target, and max prices per tier',
          pricingModeCRVDescription:
            'CRV pricing with scrap value only (no min/target/max)',
          currency: 'Currency',
          counterpartyGroup: 'Counterparty Group',
          facility: 'Facility',
          startDate: 'Start Date',
          endDate: 'End Date',
          notes: 'Notes',
        },
        placeholders: {
          name: 'e.g., Q1 2026 Premium Suppliers',
          currency: 'USD',
          counterpartyGroup: 'Select counterparty group...',
          facility: 'Select facility...',
          notes: 'Optional notes...',
        },
        submitButton: 'Create Price List',
      },
      status: {
        draft: 'Draft',
        active: 'Active',
        expired: 'Expired',
        archived: 'Archived',
      },
      table: {
        columns: {
          name: 'Name',
          operationType: 'Operation Type',
          status: 'Status',
          pricingMode: 'Pricing Mode',
          currency: 'Currency',
          startDate: 'Start Date',
          endDate: 'End Date',
          created: 'Created',
          updated: 'Updated',
        },
      },
      copyFrom: {
        sourceType: 'Source Type',
        defaultPriceList: 'Default Price List',
        customPriceList: 'Custom Price List',
        selectSource: 'Select Source',
        sourcePriceList: 'Source Price List',
        copyButton: 'Copy Pricings',
      },
      importFrom: {
        selectSource: 'Select Source',
        sourcePriceList: 'Source Price List',
        selectItems: 'Select Items to Import',
        selectAll: 'Select All',
        importButton: 'Import Selected',
      },
      duplicate: {
        copyFromField: 'Copy pricings from',
        placeholder: 'Select a price list to copy from...',
      },
      toast: {
        created: 'Draft price list created',
        updated: 'Price list updated',
        published: 'Price list published',
        archived: 'Price list archived',
        unarchived: 'Price list unarchived',
        deleted: 'Price list deleted',
        duplicated: 'Price list duplicated',
        copiedFrom: 'Pricings copied successfully',
        importedFrom: 'Pricings imported successfully',
        createError: (error: string) => `Failed to create price list: ${error}`,
        publishError: (error: string) =>
          `Failed to publish price list: ${error}`,
        archiveError: (error: string) =>
          `Failed to archive price list: ${error}`,
        deleteError: (error: string) => `Failed to delete price list: ${error}`,
        duplicateError: (error: string) =>
          `Failed to duplicate price list: ${error}`,
        copyFromError: (error: string) => `Failed to copy pricings: ${error}`,
        importFromError: (error: string) =>
          `Failed to import pricings: ${error}`,
        pdfGenerated: 'PDF generated successfully',
        pdfError: (error: string) => `Failed to generate PDF: ${error}`,
        emailSent: 'Email sent successfully',
        emailError: (error: string) => `Failed to send email: ${error}`,
      },
      history: {
        title: 'Price List History',
        empty: 'No history entries found.',
      },
      email: {
        title: 'Send Price List via Email',
        recipients: 'Recipients',
        addRecipient: 'Enter email address...',
        subject: 'Subject',
        sendButton: 'Send Email',
      },
      validation: {
        nameRequired: 'Name is required',
        counterpartyGroupRequired: 'Counterparty group is required',
        facilityRequired: 'Facility is required',
      },
    },
    itemPricings: {
      addButton: 'Add Product Pricing',
      table: {
        columns: {
          product: 'Material',
          pricingUnit: 'Price Unit',
          minWeight: 'Min Weight',
          maxAmount: 'Max Weight',
          targetPrice: 'Target Price',
          minPrice: 'Min Price',
          maxPrice: 'Max Price',
          scrapValue: 'Scrap Value',
        },
        tieredPricing: '(Tiered Pricing)',
        selectMaterial: 'Select material...',
        addTier: 'Add Tier',
        copyItem: 'Copy',
        deleteItem: 'Delete',
      },
      deleteConfirm:
        'Are you sure you want to delete this product pricing? This action cannot be undone.',
      toast: {
        added: 'Product pricing added',
        updated: 'Product pricing updated',
        deleted: 'Product pricing deleted',
        tierAdded: 'Tier added successfully',
        error: (error: string) => `Operation failed: ${error}`,
      },
    },
    formulaUI: {
      variablesPanel: {
        title: 'Variables',
        empty:
          'No variables defined. Create variables to use in pricing formulas.',
        manageVariables: 'Manage Variables',
      },
      variablesButton: 'Variables',
    },
    groups: {
      ungrouped: 'Ungrouped',
    },
    search: {
      placeholder: 'Search materials...',
    },
    bulk: {
      selectAll: 'Select All',
    },
    asOf: {
      label: 'View as of',
      timeLabel: 'Time',
      clearButton: 'Show current prices',
      historicalIndicator: 'Viewing historical prices',
      futureIndicator: 'Viewing scheduled prices',
    },
    saveToolbar: {
      unsavedChanges: (count: number) =>
        `${count} unsaved change${count !== 1 ? 's' : ''}`,
      reviewMessage:
        'Changes have been made. To activate changes proceed with review.',
      reviewAndSave: 'Review & Save',
      effectiveDate: 'Effective date',
      effectiveTime: 'Effective time',
      save: 'Save changes',
      activate: 'Activate Changes',
      revert: 'Revert Changes',
      discard: 'Discard',
      confirmRevert: {
        title: 'Revert Changes',
        description:
          'Changes will be reversed after confirming this action. Do you want to proceed?',
        confirm: 'Revert all',
        keep: 'No, keep',
      },
      saved: 'Changes saved successfully',
      minExceedsTarget:
        'Min price cannot exceed target price. Check your tier values.',
      maxBelowTarget:
        'Max price cannot be less than target price. Check your tier values.',
      targetPriceRequired: 'Target price is required for all tiers.',
      lastTierMustBeUnbounded:
        'The last tier for each material must have no max weight (unlimited).',
      duplicateItem:
        'Duplicate material and unit combination detected. Each material can only appear once per unit.',
      incompleteNewRow:
        'Please select both a material and a pricing unit for all new rows.',
    },
    variableSaveToolbar: {
      reviewMessage: 'pending variable changes to review',
      reviewAndSave: 'Review & Save',
      activate: 'Activate',
      revert: 'Revert',
      effectiveTime: 'Effective time',
      confirmRevert: {
        description: 'Are you sure you want to discard all pending changes?',
        keep: 'Keep Changes',
        confirm: 'Discard All',
      },
      review: {
        variable: 'Variable',
        oldValue: 'Current',
        newValue: 'New Value',
      },
    },
    validation: {
      title: 'Validation Issues',
      error: (count: number) => `${count} ${count === 1 ? 'error' : 'errors'}`,
      warning: (count: number) =>
        `${count} ${count === 1 ? 'warning' : 'warnings'}`,
      tier: (n: number) => `Tier ${n}`,
    },
  },
};
